// @inheritedComponent IconButton
import * as React from 'react'
import clsx from 'clsx'
import { SwitchBaseProps, SwitchBase } from '../SwitchBase'
import { SxProps, sx as sxFunc } from '../sx'
import style from './index.module.scss'
import { capitalize } from '../../utils'
import { useFormControl } from '../FormControl'

export interface SwitchClasses {
  /** Styles applied to the root element. */
  root: string
  /** Styles applied to the root element if `size="small"`. */
  sizeSmall: string
  /** Styles applied to the root element if `size="medium"`. */
  sizeMedium: string
  /** Styles applied to the root element if `size="large"`. */
  sizeLarge: string
  /** Styles used to create the thumb passed to the internal `SwitchBase` component `icon` prop. */
  thumb: string
  /** Styles applied to the track element. */
  track: string
  /** Styles applied to the internal `SwitchBase` component's `root` class. */
  switchBase: string
  /** State class applied to the internal `SwitchBase` component's `checked` class. */
  checked: string
  /** State class applied to the internal SwitchBase component's disabled class. */
  disabled: string
  /** Styles applied to the internal SwitchBase component's input element. */
  input: string
  /** Styles applied to the root element if `edge="start"`. */
  edgeStart: string
  /** Styles applied to the root element if `edge="end"`. */
  edgeEnd: string
}

export interface SwitchProps extends SwitchBaseProps {
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<SwitchClasses>
  /**
   * The color of the component.
   * @default 'primary'
   */
  color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
  /**
   * The size of the component.
   * `small` is equivalent to the dense switch styling.
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large'
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
}

export const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(
  function Switch(props, ref) {
    const {
      className,
      classes,
      color = 'primary',
      size = 'medium',
      disabled: disabledProp,
      sx,
      ...other
    } = props

    const factorFormControl = useFormControl()

    let disabled = disabledProp
    if (factorFormControl) {
      if (typeof disabled === 'undefined') {
        disabled = factorFormControl.disabled
      }
    }

    const icon = <span className={clsx(style.switchThumb, classes?.thumb)} />

    return (
      <span
        className={clsx(
          style.root,
          classes?.root,
          style[`size--${size}`],
          disabled && style['disabled'],
          // @ts-ignore
          classes?.[`size${capitalize(size)}`],
          className,
          sxFunc(sx)
        )}
      >
        <SwitchBase
          type="checkbox"
          icon={icon}
          checkedIcon={icon}
          ref={ref}
          disabled={disabled}
          {...other}
          className={style[`color--${color}`]}
          classes={{
            root: clsx(style['switchBase'], classes?.switchBase),
            edgeStart: clsx(style['edge--start'], classes?.edgeStart),
            edgeEnd: clsx(style['edge--end'], classes?.edgeEnd),
            input: clsx(style['switchInput'], classes?.input),
            checked: clsx(style['checked'], classes?.checked),
            disabled: classes?.disabled,
          }}
        />
        <span className={clsx(style['switchTrack'], classes?.track)} />
      </span>
    )
  }
)
